const addons = new Map();

function assertCapabilities(value, label) {
  if (value === undefined) return;
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string" || !item)) {
    throw new Error(`Invalid requiredCapabilities in ${label}`);
  }
}

function validateAddon(definition) {
  if (!definition || definition.abiVersion !== 1) throw new Error("Unsupported LEN0X frontend addon ABI");
  if (!/^[a-z0-9]+(?:[._-][a-z0-9]+)+$/.test(definition.addonId || "")) throw new Error("Invalid addonId");
  if (!definition.addonVersion) throw new Error("Missing addonVersion");
  assertCapabilities(definition.requiredCapabilities, definition.addonId);

  const panelIds = new Set();
  for (const panel of definition.panels || []) {
    if (!panel?.id || typeof panel.component !== "function") throw new Error(`Invalid panel in ${definition.addonId}`);
    if (panelIds.has(panel.id)) throw new Error(`Duplicate addon panel ${panel.id}`);
    assertCapabilities(panel.requiredCapabilities, `${definition.addonId}:${panel.id}`);
    panelIds.add(panel.id);
  }

  const navigationIds = new Set();
  for (const item of definition.navigation || []) {
    if (!item?.id || !item?.label || !panelIds.has(item.panelId || item.id)) throw new Error(`Navigation without panel in ${definition.addonId}`);
    if (navigationIds.has(item.id)) throw new Error(`Duplicate addon navigation ${item.id}`);
    assertCapabilities(item.requiredCapabilities, `${definition.addonId}:${item.id}`);
    navigationIds.add(item.id);
  }

  const routeIds = new Set();
  for (const route of definition.routes || []) {
    if (!route?.id || !route?.path || !panelIds.has(route.panelId)) throw new Error(`Invalid route in ${definition.addonId}`);
    if (routeIds.has(route.id)) throw new Error(`Duplicate addon route ${route.id}`);
    assertCapabilities(route.requiredCapabilities, `${definition.addonId}:${route.id}`);
    routeIds.add(route.id);
  }

  for (const extension of definition.apiExtensions || []) {
    if (!extension?.key || !extension?.path) throw new Error(`Invalid API extension in ${definition.addonId}`);
    if (!String(extension.path).startsWith(`/addons/${definition.addonId}`)) throw new Error(`API extension outside addon namespace in ${definition.addonId}`);
  }
}

export function registerAddon(definition) {
  validateAddon(definition);
  const previous = addons.get(definition.addonId);
  if (previous && previous.addonVersion !== definition.addonVersion) throw new Error(`Addon already registered: ${definition.addonId}`);
  addons.set(definition.addonId, Object.freeze({ ...definition }));
  return definition;
}

export function registeredAddons() {
  return Array.from(addons.values()).sort((a, b) => a.addonId.localeCompare(b.addonId));
}

function runtimeMap(runtime) {
  return new Map((runtime?.addons || []).map((item) => [item.addon_id, item]));
}

function allowed(item, runtimeItem) {
  if (!runtimeItem || runtimeItem.status !== "active" || !runtimeItem.enabled) return false;
  const available = new Set(runtimeItem.capabilities || []);
  return (item.requiredCapabilities || []).every((key) => available.has(key));
}

export function addonNavigation(runtime) {
  const current = runtimeMap(runtime);
  return registeredAddons().flatMap((addon) => {
    const runtimeItem = current.get(addon.addonId);
    if (!runtimeItem || runtimeItem.version !== addon.addonVersion) return [];
    return (addon.navigation || []).filter((item) => allowed(item, runtimeItem)).map((item) => ({
      ...item,
      id: `addon:${addon.addonId}:${item.id}`,
      addonId: addon.addonId,
      panelId: item.panelId || item.id,
      addonStatus: runtimeItem,
      group: item.group || "addons",
      order: Number.isFinite(Number(item.order)) ? Number(item.order) : 100,
    }));
  }).sort((a, b) => String(a.group).localeCompare(String(b.group)) || a.order - b.order || a.label.localeCompare(b.label));
}

export function resolveAddonPanel(view, runtime) {
  if (!view?.startsWith("addon:")) return null;
  const [, addonId, panelId] = view.split(":");
  const addon = addons.get(addonId);
  const runtimeItem = runtimeMap(runtime).get(addonId);
  if (!addon || !runtimeItem || runtimeItem.version !== addon.addonVersion) return null;
  const panel = (addon.panels || []).find((item) => item.id === panelId);
  return panel && allowed(panel, runtimeItem) ? { addon, panel, runtime: runtimeItem } : null;
}

export function clearAddonRegistryForTests() {
  addons.clear();
}
